/**
 * The vfx surface's report (ADR-134): its switch, its counts and a reader of the vfx running, which a browser test reads
 * through the game report, as the emoji surface's is read.
 */

/**
 * A box in logical pixels of the UI layer.
 */
export interface BurstBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * One vfx running, as the last frame drew it: which, on whom, how long it has played, the tint's mid tone, and the boxes
 * of its parts that are shown (null for one that is not).
 */
export interface VfxReading {
  readonly id: string;
  readonly handle: number;
  readonly own: boolean;
  readonly elapsed: number;
  readonly shown: boolean;
  readonly pillar: BurstBox | null;
  readonly ring: BurstBox | null;
  readonly sign: BurstBox | null;
  readonly aura: BurstBox | null;
  readonly rays: number;
  readonly sparks: number;
  readonly reduced: boolean;
  readonly tint: number;
}

/**
 * The surface's report.
 */
export interface VfxReport {
  /** Whether vfx are drawn at all; a test turns it off to see a page without them. */
  enabled: boolean;
  /** Vfx running now. */
  active: number;
  /** Vfx started since the page loaded. */
  started: number;
  /** Slots of images made; at most the cap. */
  created: number;
  /** Vfx drawn on the last frame. */
  shown: number;
  /** The edge glow's step on the last frame, 0 for none. */
  edgeStep: number;
  /** Reads the vfx running, on request. */
  list: () => readonly VfxReading[];
}

/**
 * The report of a page that has not made the surface yet.
 *
 * @returns The report.
 */
export function createVfxReport(): VfxReport {
  return {
    enabled: true,
    active: 0,
    started: 0,
    created: 0,
    shown: 0,
    edgeStep: 0,
    /**
     * Lists no vfx until the surface is created and replaces this.
     *
     * @returns No readings.
     */
    list: () => [],
  };
}
